import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import * as Analytics from 'analytics/analytics'
import { promotionClickEvent } from 'analytics/snowplow/events'
import config from 'constants/config'
import {
  MALDIVES_BUNDLE_DEFAULT_NIGHTS,
  MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF,
} from 'constants/charterFlight'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

import TextButton from 'LuxKit/Button/TextButton'
import Pill from 'LuxKit/Pill'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyText from 'LuxKit/Typography/BodyText'

function MaldivesDirectBundleBanner() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)

  const exclusiveBundleLabel = intl.formatMessage({
    id: 'ZvcJmU3S4S',
    defaultMessage: 'Exclusive bundle',
    description: 'Badge text for Maldives direct charter bundle offer',
  })

  const viewBundleOfferLabel = intl.formatMessage({
    id: 'p3A5zgTrb5',
    defaultMessage: 'View bundle offer',
    description: 'Button text to view the Maldives charter bundle offer details',
  })

  const flyDirectDescription = intl.formatMessage({
    id: 'tFUMy353P2',
    defaultMessage: 'Fly direct from Melbourne to Malé, exclusively chartered for Luxury Escapes customers.',
    description: 'Description text for Maldives direct charter bundle banner',
  })

  const priceCopy = useMemo(() => {
    return `$${MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON.toLocaleString('en-AU')}`
  }, [])

  const campaignUrl = useMemo(() => {
    return `/${currentRegionCode.toLowerCase()}/campaign/maldives-direct`
  }, [currentRegionCode])

  const handleViewBundleClick = useCallback(() => {
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: 'Maldives Direct Banner (Offer Page)',
        promotionType: 'banner',
      },
    }))
  }, [])

  if (currentRegionCode !== 'AU' && !(config.agentHub.isEnabled && currentRegionCode === 'NZ')) {
    return null
  }

  return (
    <Sheet background="highlight-primary" borderRadius="M" padding="16 20">
      <Group direction="vertical" tabletDirection="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between">
        <Group direction="vertical" tabletDirection="horizontal" gap={2} tabletGap={12} tabletVerticalAlign="start">
          <CSSBreakpoint max="mobile">
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
              <Pill kind="primary" variant="success">{exclusiveBundleLabel}</Pill>
              <LinePlaneDepartureIcon size="L" />
            </Group>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <LinePlaneDepartureIcon size="L" />
          </CSSBreakpoint>
          <Group direction="vertical" gap={4}>
            <Group direction="vertical" tabletDirection="horizontal" gap={4} tabletVerticalAlign="center" wrap="wrap" desktopGap={4}>
              <CSSBreakpoint min="tablet">
                <Pill kind="primary" variant="success">{exclusiveBundleLabel}</Pill>
              </CSSBreakpoint>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'wiC06tA0kC',
                  defaultMessage: 'Return flights + {nights}-night stay',
                  description: 'Maldives direct bundle banner describing return flights plus hotel stay duration',
                }, { nights: MALDIVES_BUNDLE_DEFAULT_NIGHTS })}
              </BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                {/* TODO: i18n - complex pricing format */}
                <BodyText variant="medium" weight="bold" wrap="no-wrap">from</BodyText>
                <BodyText variant="large" weight="bold" colour="highlight-secondary" wrap="no-wrap">
                  {/* TODO: i18n - complex pricing format */}
                  {priceCopy}/adult
                </BodyText>
              </Group>
            </Group>
            <BodyText variant="small" colour="neutral-two">
              {flyDirectDescription}
            </BodyText>
          </Group>
        </Group>

        <Group direction="horizontal" gap={8}>
          <TextButton
            kind="secondary"
            startIcon={<LinePhoneIcon />}
            href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
            rel="nofollow"
          >
            {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
          </TextButton>
          <TextButton
            kind="primary"
            href={campaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleViewBundleClick}
          >
            {viewBundleOfferLabel}
          </TextButton>
        </Group>
      </Group>
    </Sheet>
  )
}

export default React.memo(MaldivesDirectBundleBanner)
